import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { controllers as controllerTemplates } from '../../controllers';
import { addPublicURL } from '../../Functions/AddPublicURL';
import { formatEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { getAccessToken } from '../../Functions/LocalStorageUtils';
import { useCheckIconsConfigurationMutation, useStartIconsConfigurationMutation } from '../../redux/api/configurations';
import { setSelectedActuator } from '../../redux/reducers/actuatorsRedurer';
import { connectApplianceToController, disconnectApplianceFromController, setActuatorControllerLinks, setShowActuatorDetail, setShowThermostatDetail } from '../../redux/reducers/appReducer';
import { setControllerConnectedActuator, setSelectedDevice, setSelectedDeviceConfiguredImageName, setSelectedDeviceIconsData } from '../../redux/reducers/controllersReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { IconsI } from '../../redux/types/controllers';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import LoadingCircle from '../LoadingCircle/LoadingCircle';

import styles from './ThermostatDetail.module.scss';

const thermostatApplianceIcons: Record<string, string[]> = {
    fancoil: ['icon_fancoil'],
    electric: ['11B'],
    radiator: ['11A'],
    vrv: ['11K']
};

const ThermostatDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const controllerId = useAppSelector((state) => state.controllers.selectedDevice.id);
    const controllers = useAppSelector((state) => state.controllers.devices);
    const appliances = useAppSelector((state) => state.app.appliances);
    const actuators = useAppSelector((state) => state.app.actuators);
    const controller = useAppSelector((state) => state.controllers.selectedDevice);
    const selectedControllerId = controller.id;
    const dragPayloadRef = useRef<{ source: 'controller', slotIndex?: number } | null>(null);
    const didDropRef = useRef(false);
    const iconWrapperRef = useRef<HTMLDivElement | null>(null);
    const renderImageName = controller.configuredImageName
        ? `${process.env.REACT_APP_ICONS_URL}/source/userpanels/${controller.configuredImageName}`
        : addPublicURL(`/assets/controllers/render/${controller.renderImage}-${controller.color}.png`);
    const [creatingDesign, setCreatingDesign] = useState(false);
    const [startIconsConfiguration] = useStartIconsConfigurationMutation();
    const [checkIconsConfiguration] = useCheckIconsConfigurationMutation();
    const thermostatAppliance = useMemo(() => {
        return controllerTemplates.find((template) =>
            template.type === 'thermostat' && template.name === controller.name
        )?.thermostatAppliance;
    }, [controller.name]);
    const requiredActuators = useMemo(() => {
        const requiredType = controller.thermostatRequiredActuator;
        if (!requiredType) {
            return [];
        }

        const maxCount = controller.thermostatRequiredActuatorMaxCount ?? Number.POSITIVE_INFINITY;
        const selectedActuatorIds = controller.connectedActuatorIds ?? (controller.connectedActuator === undefined ? [] : [controller.connectedActuator]);
        return actuators.filter((actuator) => {
            if (actuator.type !== requiredType) {
                return false;
            }

            return selectedActuatorIds.includes(actuator.id ?? -1) || selectedActuatorIds.length < maxCount;
        });
    }, [actuators, controller.connectedActuator, controller.connectedActuatorIds, controller.thermostatRequiredActuator, controller.thermostatRequiredActuatorMaxCount]);
    const hasRequiredActuator = controller.thermostatRequiredActuator !== null
        && controller.thermostatRequiredActuator !== undefined;

    const isCompatibleAppliance = useCallback((appliance: typeof appliances[number]) => {
        if (appliance.applianceType !== 'hvac') {
            return false;
        }

        const compatibleIcons = thermostatAppliance
            ? thermostatApplianceIcons[thermostatAppliance]
            : undefined;

        return !compatibleIcons || compatibleIcons.includes(appliance.icon);
    }, [thermostatAppliance]);

    const thermostatControllerIds = useMemo(() => {
        return new Set(
            controllers
                .filter((device) => device.type === 'thermostat')
                .map((device) => device.id)
        );
    }, [controllers]);

    const applianceConnectedToCurrentThermostat = useMemo(() => {
        return appliances.find((appliance) => {
            if(!isCompatibleAppliance(appliance)){
                return false;
            }
            return appliance.connectedTo?.includes(controller.id);
        });
    }, [appliances, controller.id, isCompatibleAppliance]);

    const getSlotApplianceIds = (slot: IconsI): number[] => slot.applianceId ?? [];
    const isUnassignedSlot = (slot: IconsI) => getSlotApplianceIds(slot).length === 0;

    const syncApplianceConnections = useCallback((prevIconsData: IconsI[], nextIconsData: IconsI[]) => {
        if(!selectedControllerId){
            return;
        }

        const prevIds = new Set(
            prevIconsData
                .flatMap((slot) => getSlotApplianceIds(slot))
        );
        const nextIds = new Set(
            nextIconsData
                .flatMap((slot) => getSlotApplianceIds(slot))
        );

        nextIds.forEach((applianceId) => {
            if(!prevIds.has(applianceId)){
                dispatch(connectApplianceToController({ applianceId, controllerId: selectedControllerId }));
            }
        });
        prevIds.forEach((applianceId) => {
            if(!nextIds.has(applianceId)){
                dispatch(disconnectApplianceFromController({ applianceId, controllerId: selectedControllerId }));
            }
        });
    }, [dispatch, selectedControllerId]);

    useEffect(() => {
        if(controllerId && controllers.length > 0){
            const device = controllers.find((device) => device.id === controllerId);
            if(!device){
                toast.error(t('thermostatDetail.noDeviceSelected'));
                dispatch(setShowThermostatDetail(false));
                return;
            }
            dispatch(setSelectedDevice({
                id: device.id,
                name: device.name,
                tech: device.tech,
                type: device.type,
                material: device.material,
                icons: device.icons,
                round: device.round,
                color: device.color,
                colorShortcut: device.colorShortcut,
                className: device.className,
                previewImage: device.previewImage,
                renderImage: device.renderImage,
                configuredImageName: device.configuredImageName,
                state: device.state,
                price: device.price || 0,
                iconsData: device.iconsData,
                connectedActuator: device.connectedActuator,
                connectedActuatorIds: device.connectedActuatorIds,
                thermostatAppliance: device.thermostatAppliance,
                thermostatRequiredActuator: device.thermostatRequiredActuator,
                thermostatRequiredActuatorMaxCount: device.thermostatRequiredActuatorMaxCount
            }));
        }else{
            toast.error(t('thermostatDetail.noDeviceSelected'));
            dispatch(setShowThermostatDetail(false));
        }
    }, [controllerId, controllers, dispatch, t]);

    useEffect(() => {
        const revertSlotToDefault = (slotIndex: number) => {
            const prevIconsData = controller.iconsData;
            const nextIconsData = controller.iconsData.map((item, index) => {
                if(index !== slotIndex){
                    return item;
                }
                return {
                    ...item,
                    applianceId: undefined
                };
            });
            dispatch(setSelectedDeviceIconsData(nextIconsData));
            syncApplianceConnections(prevIconsData, nextIconsData);
        };

        const handleWindowDragOver = (event: DragEvent) => {
            const payload = dragPayloadRef.current;
            if(payload?.source !== 'controller'){
                return;
            }
            event.preventDefault();
        };

        const handleWindowDrop = (event: DragEvent) => {
            const payload = dragPayloadRef.current;
            if(payload?.source !== 'controller' || payload.slotIndex === undefined){
                return;
            }

            event.preventDefault();

            const dropTarget = event.target;
            const droppedOnSlotImg = !!(
                iconWrapperRef.current &&
                dropTarget instanceof HTMLImageElement &&
                iconWrapperRef.current.contains(dropTarget)
            );

            if(!droppedOnSlotImg){
                revertSlotToDefault(payload.slotIndex);
            }

            didDropRef.current = true;
            dragPayloadRef.current = null;
        };

        window.addEventListener('dragover', handleWindowDragOver);
        window.addEventListener('drop', handleWindowDrop);

        return () => {
            window.removeEventListener('dragover', handleWindowDragOver);
            window.removeEventListener('drop', handleWindowDrop);
        };
    }, [controller.iconsData, dispatch, syncApplianceConnections]);

    const handleCheckboxClick = (applianceId: number) => () => {
        const prevIconsData = controller.iconsData;
        const assignedSlotIndex = prevIconsData.findIndex((slot) => (slot.applianceId ?? []).includes(applianceId));
        if(assignedSlotIndex !== -1){
            const nextIconsData = prevIconsData.map((item, index) => {
                if(index !== assignedSlotIndex){
                    return item;
                }
                return {
                    ...item,
                    applianceId: undefined
                };
            });
            dispatch(setSelectedDeviceIconsData(nextIconsData));
            syncApplianceConnections(prevIconsData, nextIconsData);
            return;
        }

        const existingAssignedSlotIndex = prevIconsData.findIndex((slot) => !isUnassignedSlot(slot));
        const targetSlotIndex = existingAssignedSlotIndex !== -1
            ? existingAssignedSlotIndex
            : prevIconsData.findIndex((slot) => isUnassignedSlot(slot));

        if(targetSlotIndex === -1){
            return;
        }

        const nextIconsData = prevIconsData.map((item, index) => {
            if(index !== targetSlotIndex){
                return item;
            }
            return {
                ...item,
                applianceId: [applianceId]
            };
        });

        dispatch(setSelectedDeviceIconsData(nextIconsData));
        syncApplianceConnections(prevIconsData, nextIconsData);
    };

    const handleRequiredActuatorConnection = (actuatorId: number | undefined) => () => {
        if (actuatorId === undefined) {
            return;
        }

        const selectedActuatorIds = controller.connectedActuatorIds ?? (controller.connectedActuator === undefined ? [] : [controller.connectedActuator]);
        const nextActuatorIds = selectedActuatorIds.includes(actuatorId)
            ? selectedActuatorIds.filter((id) => id !== actuatorId)
            : [...selectedActuatorIds, actuatorId];
        dispatch(setControllerConnectedActuator({
            controllerId: controller.id,
            actuatorIds: nextActuatorIds
        }));
        dispatch(setActuatorControllerLinks(
            controllers.map((device) =>
                device.id === controller.id
                    ? {
                        ...device,
                        connectedActuator: nextActuatorIds[0],
                        connectedActuatorIds: nextActuatorIds
                    }
                    : device
            )
        ));
    };

    const handleRequiredActuatorClick = (requiredActuator: typeof actuators[number]) => () => {
        if (!requiredActuator) {
            return;
        }

        dispatch(setSelectedActuator(requiredActuator));
        dispatch(setShowThermostatDetail(false));
        dispatch(setShowActuatorDetail(true));
    };

    const handleControllerDragStart = (slotIndex: number) => (event: React.DragEvent<HTMLImageElement>) => {
        didDropRef.current = false;
        dragPayloadRef.current = { source: 'controller', slotIndex };
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('application/x-floorplan-icon', JSON.stringify({ source: 'controller', slotIndex }));
    };

    const handleControllerDragEnd = () => {
        const payload = dragPayloadRef.current;
        if(payload?.source !== 'controller' || payload.slotIndex === undefined){
            dragPayloadRef.current = null;
            return;
        }

        if(didDropRef.current){
            dragPayloadRef.current = null;
            return;
        }

        const prevIconsData = controller.iconsData;
        const nextIconsData = controller.iconsData.map((item, index) => {
            if(index !== payload.slotIndex){
                return item;
            }
            return {
                ...item,
                applianceId: undefined
            };
        });
        dispatch(setSelectedDeviceIconsData(nextIconsData));
        syncApplianceConnections(prevIconsData, nextIconsData);
        dragPayloadRef.current = null;
    };

    const handleControllerSlotDragOver = (event: React.DragEvent<HTMLImageElement>) => {
        const payload = dragPayloadRef.current;
        if(payload?.source !== 'controller'){
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const handleControllerSlotDrop = (slotIndex: number) => (event: React.DragEvent<HTMLImageElement>) => {
        const raw = event.dataTransfer.getData('application/x-floorplan-icon');
        if(!raw){
            return;
        }

        let payload: { source: 'controller', slotIndex?: number } | null = null;
        try{
            payload = JSON.parse(raw);
        }catch{
            payload = null;
        }

        event.preventDefault();

        if(payload?.source === 'controller'){
            const fromIndex = payload.slotIndex;
            if(fromIndex === undefined || fromIndex === slotIndex){
                didDropRef.current = true;
                return;
            }

            const fromSlot = controller.iconsData[fromIndex];
            const toSlot = controller.iconsData[slotIndex];
            const fromApplianceId = fromSlot?.applianceId;
            const toApplianceId = toSlot?.applianceId;

            const nextIconsData = controller.iconsData.map((item, index) => {
                if(index === fromIndex){
                    return {
                        ...item,
                        applianceId: toApplianceId
                    };
                }
                if(index === slotIndex){
                    return {
                        ...item,
                        applianceId: fromApplianceId
                    };
                }
                return item;
            });

            didDropRef.current = true;
            dispatch(setSelectedDeviceIconsData(nextIconsData));
        }
    };

    useEffect(() => {
        if(!creatingDesign) return;

        const interval = window.setInterval(() => {
            void (async () => {
                try {
                    const result = await checkIconsConfiguration().unwrap();
                    if(result.success){
                        dispatch(setSelectedDeviceConfiguredImageName(result.image));
                        setCreatingDesign(false);
                    }
                } catch(error){
                    console.error(error);
                }
            })();
        }, 5000);

        return () => window.clearInterval(interval);
    }, [creatingDesign, checkIconsConfiguration, dispatch]);

    const handleCreateDesign = async () => {
        if(creatingDesign) return;
        if(!getAccessToken()) return;
        await startIconsConfiguration({
            type: controller.name,
            color: controller.color
        });
        setCreatingDesign(true);
    };

    return(
        <>
            {creatingDesign && (
                <div className={styles.ThermostatDetail__designOverlay}>
                    <div className={styles.ThermostatDetail__designOverlay__wrapper}>
                        <h2>{t('thermostatDetail.designOverlay.heading')}</h2>
                        <p>{t('thermostatDetail.designOverlay.description')}</p>
                        <LoadingCircle size={48} thickness={4} color='primary' className={styles.ThermostatDetail__designOverlay__spinner} />
                        <Button variant='fill' size='lg' onClick={() => setCreatingDesign(false)}>{t('common.cancel')}</Button>
                    </div>
                </div>
            )}
            <div className={styles.ThermostatDetail}>
                <div className={styles.ThermostatDetail__wrapper}>
                    <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setShowThermostatDetail(false))} className={styles.ThermostatDetail__back} />
                    <h2>{t('thermostatDetail.title')}</h2>
                    <p>{t('thermostatDetail.description')}</p>
                    <div className={styles.ThermostatDetail__container}>
                        <div className={styles.ThermostatDetail__container__appliances}>
                            <h3>{hasRequiredActuator ? t('thermostatDetail.requiredActuator.heading') : t('thermostatDetail.appliances.heading')}</h3>
                            {!hasRequiredActuator && (
                                <div className={styles.ThermostatDetail__container__appliances__heading}>
                                    <p>{t('thermostatDetail.appliances.select')}</p>
                                    <span></span>
                                    <p>{t('thermostatDetail.appliances.id')}</p>
                                    <p>{t('thermostatDetail.appliances.name')}</p>
                                    <p>{t('thermostatDetail.appliances.status')}</p>
                                </div>
                            )}
                            <div className={styles.ThermostatDetail__container__appliances__list}>
                                {hasRequiredActuator ? (
                                    requiredActuators.length > 0 ? (
                                        requiredActuators.map((requiredActuator) => (
                                            <div key={requiredActuator.id} className={styles.ThermostatDetail__container__appliances__list__requiredActuator}>
                                                <Checkbox
                                                    checked={(controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(requiredActuator.id)}
                                                    onChange={handleRequiredActuatorConnection(requiredActuator.id)}
                                                />
                                                <div onClick={handleRequiredActuatorClick(requiredActuator)}>
                                                    <img src={addPublicURL(`assets/actuators/${requiredActuator.refImg}`)} alt={requiredActuator.type} />
                                                    <p>{requiredActuator.type} | {requiredActuator.name}</p>
                                                    <p>{formatEntityId('actuator', requiredActuator.id ?? 0)}</p>
                                                    <img className={styles.ThermostatDetail__container__appliances__list__requiredActuator__state} src={getIconPath(requiredActuator.state ?? 'error')} alt={requiredActuator.state} />
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p>{t('thermostatDetail.requiredActuator.unavailable')}</p>
                                    )
                                ) : appliances.map((appliance) =>
                                    isCompatibleAppliance(appliance) &&
                                    (applianceConnectedToCurrentThermostat
                                        ? appliance.id === applianceConnectedToCurrentThermostat.id
                                        : (appliance.connectedTo ?? [])
                                            .filter((connectedControllerId) => thermostatControllerIds.has(connectedControllerId))
                                            .every((connectedControllerId) => connectedControllerId === controller.id)
                                    ) && (
                                        <div key={appliance.id} className={styles.ThermostatDetail__container__appliances__list__item}>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <Checkbox checked={controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(appliance.id))} onChange={handleCheckboxClick(appliance.id)}></Checkbox>
                                            </div>
                                            <img
                                                className={styles.ThermostatDetail__container__appliances__list__item__icon}
                                                src={getIconPath(appliance.icon, '', 'controllers/black')}
                                                alt={appliance.icon}
                                                draggable={false}
                                            />
                                            <p>{formatEntityId('appliance', appliance.id)}</p>
                                            <p>{appliance.label}</p>
                                            {appliance.state != undefined && (
                                                <img className={styles.ThermostatDetail__container__appliances__list__item__state} src={getIconPath(appliance.state)} alt={appliance.state} />
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                        <span></span>
                        <div className={styles.ThermostatDetail__container__controller}>
                            <h3>{t('thermostatDetail.controllers.heading')}</h3>
                            <div className={styles.ThermostatDetail__container__controller__content}>
                                <img src={renderImageName} alt="Controller render" />
                                <div
                                    className={classNames(styles.iconWrapper, styles[controller.className])}
                                    ref={iconWrapperRef}
                                >
                                    {controller.configuredImageName === undefined && controller.iconsData.map((iconsData, index) => (
                                        <div className={classNames(styles.controllerIcon, styles[`img-${index}`])} key={index}>
                                            <img
                                                key={index}
                                                src={getIconPath(iconsData.icon)}
                                                alt={iconsData.icon}
                                                draggable={(iconsData.applianceId ?? []).length > 0}
                                                onDragStart={(iconsData.applianceId ?? []).length > 0 ? handleControllerDragStart(index) : undefined}
                                                onDragEnd={(iconsData.applianceId ?? []).length > 0 ? handleControllerDragEnd : undefined}
                                                onDragOver={handleControllerSlotDragOver}
                                                onDrop={handleControllerSlotDrop(index)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.ThermostatDetail__container__controller__info}>
                                <p>{controller?.name + (controller?.colorShortcut ?? 'Unknown')} | {formatEntityId('controller', controller?.id)}</p>
                            </div>
                            {getAccessToken() && controller?.name !== 'RFTC-9/' && (
                                <div className={styles.ThermostatDetail__container__controller__design}>
                                    <h4>{t('thermostatDetail.controllers.design')}</h4>
                                    <Button variant='fill' size='lg' onClick={() => handleCreateDesign()}>{t('thermostatDetail.controllers.designButton')}</Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ThermostatDetail;
